import { Component, ErrorInfo, ReactNode } from "react";
import { CtaButton } from "@/components/common/CtaButton";

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  message?: string;
}

/**
 * Global error boundary — the app's error-handling strategy for render-time
 * failures. Catches errors below it, logs them, and shows a graceful fallback
 * instead of a white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, message: undefined });
    window.location.assign("/");
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="error-boundary"
          className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6 text-center"
        >
          <p className="font-mono text-sm uppercase tracking-[0.2em] text-accent">
            Error
          </p>
          <h1 className="font-heading text-3xl font-bold text-foreground sm:text-4xl">
            Something went sideways.
          </h1>
          <p className="max-w-md text-muted-foreground">
            An unexpected error occurred. Our team has been notified — please
            head back and try again.
          </p>
          <CtaButton onClick={this.handleReset} withArrow>
            Back to home
          </CtaButton>
        </div>
      );
    }
    return this.props.children;
  }
}
